import React, { useState, useEffect } from 'react';
import { FolderOpen, FileText, ChevronRight, ChevronDown, X, Loader2, Info, FileSpreadsheet, Presentation, File, Download } from 'lucide-react';

const KnowledgeModal = ({ isOpen, onClose }) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [loading, setLoading] = useState({ folders: false, files: false });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  const [detailFile, setDetailFile] = useState(null);
  const [exporting, setExporting] = useState(false);

  // 获取认证头
  const getAuthHeader = () => {
    const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
    return `${userInfo.code}@${userInfo.token}`;
  };

  // 获取文件夹列表
  const fetchFolders = async () => {
    setLoading(prev => ({ ...prev, folders: true }));
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/knowledge/folders`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      const data = await response.json();
      if (data.success) {
        setFolders(data.data);
        // 不再默认选中第一个文件夹，保持未选择状态
      }
    } catch (error) {
      console.error('获取文件夹列表失败:', error);
    } finally {
      setLoading(prev => ({ ...prev, folders: false }));
    }
  };

  // 获取文件列表
  const fetchFiles = async (folderKey, page = 1) => {
    setLoading(prev => ({ ...prev, files: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/knowledge/files?folder_key=${folderKey}&page=${page}&page_size=10`,
        { headers: { 'Authorization': getAuthHeader() } }
      );
      const data = await response.json();
      if (data.success) {
        setFiles(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('获取文件列表失败:', error);
    } finally {
      setLoading(prev => ({ ...prev, files: false }));
    }
  };

  // 导出Excel文件
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/knowledge/export`, {
        headers: { 'Authorization': getAuthHeader() }
      });

      if (!response.ok) {
        throw new Error('导出失败');
      }

      // 获取 blob 数据
      const blob = await response.blob();

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `知识库文件清单_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();

      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('导出Excel失败:', error);
      alert('导出失败，请重试');
    } finally {
      setExporting(false);
    }
  };

  // 打开时获取数据
  useEffect(() => {
    if (isOpen) {
      fetchFolders();
    }
  }, [isOpen]);

  // 切换文件夹展开状态
  const toggleFolder = (folderKey) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderKey]: !prev[folderKey]
    }));
  };

  // 选择文件夹
  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    fetchFiles(folder.folder_key, 1);
  };

  // 格式化文件大小
  const formatFileSize = (size) => {
    return size;
  };

  // 格式化上传时间
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 获取文件图标和颜色
  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase();
    const iconProps = { size: 18, className: 'mr-2 flex-shrink-0' };

    switch (type) {
      case 'pdf':
        return <FileText {...iconProps} className={`${iconProps.className} text-red-500`} />;
      case 'doc':
      case 'docx':
        return <FileText {...iconProps} className={`${iconProps.className} text-blue-500`} />;
      case 'xls':
      case 'xlsx':
        return <FileSpreadsheet {...iconProps} className={`${iconProps.className} text-green-600`} />;
      case 'ppt':
      case 'pptx':
        return <Presentation {...iconProps} className={`${iconProps.className} text-orange-500`} />;
      case 'txt':
        return <FileText {...iconProps} className={`${iconProps.className} text-gray-500`} />;
      default:
        return <File {...iconProps} className={`${iconProps.className} text-gray-400`} />;
    }
  };

  // 递归渲染文件夹树
  const renderFolderTree = (folderList, level = 0) => {
    return folderList.map(folder => (
      <div key={folder.folder_key}>
        <div
          className={`flex items-center py-2 px-2 rounded-lg cursor-pointer transition-colors duration-150 ${selectedFolder?.folder_key === folder.folder_key
            ? 'bg-orange-100 text-orange-700'
            : 'hover:bg-gray-100 text-gray-700'
            }`}
          style={{ paddingLeft: `${8 + level * 16}px` }}
          onClick={() => handleFolderSelect(folder)}
        >
          {folder.children && folder.children.length > 0 ? (
            <span
              className="mr-1 text-gray-400"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.folder_key);
              }}
            >
              {expandedFolders[folder.folder_key] ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </span>
          ) : (
            <span className="w-[14px] mr-1" />
          )}
          <FolderOpen size={16} className="mr-2 text-orange-400" />
          <span className="text-sm truncate">{folder.folder_name}</span>
        </div>
        {folder.children && folder.children.length > 0 && expandedFolders[folder.folder_key] && (
          <div>{renderFolderTree(folder.children, level + 1)}</div>
        )}
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[1100px] h-[600px] flex flex-col overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">知识库维护</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 rounded-lg transition-colors"
            >
              {exporting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              {exporting ? '导出中...' : '下载清单'}
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex flex-1 overflow-hidden">
          {/* 左侧文件夹树 */}
          <div className="w-[200px] border-r border-gray-200 overflow-y-auto p-3 bg-gray-50">
            <h3 className="text-xs font-medium text-gray-500 uppercase mb-3 px-2">文件夹</h3>
            {loading.folders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-orange-500" />
              </div>
            ) : folders.length > 0 ? (
              renderFolderTree(folders)
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">暂无文件夹</div>
            )}
          </div>

          {/* 右侧文件列表 */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* 文件列表头部 */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedFolder ? (
                    <>
                      <span className="font-medium">{selectedFolder.folder_name}</span>
                      <span className="ml-2 text-gray-400">({pagination.total} 个文件)</span>
                    </>
                  ) : (
                    '请选择文件夹'
                  )}
                </span>
              </div>
            </div>

            {/* 文件列表 */}
            <div className="flex-1 overflow-y-auto">
              {loading.files ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center mb-4">
                    <Loader2 size={24} className="animate-spin text-orange-500" />
                  </div>
                  <p className="text-gray-600 font-medium mb-1">正在加载文件列表</p>
                  <p className="text-gray-400 text-sm">请稍候...</p>
                </div>
              ) : files.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                      <th className="px-6 py-3">文件名</th>
                      <th className="px-6 py-3 w-24">类型</th>
                      <th className="px-6 py-3 w-24">大小</th>
                      <th className="px-6 py-3 w-50">上传时间</th>
                      <th className="px-6 py-3 w-20">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {files.map(file => (
                      <tr key={file.file_key} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3">
                          <span
                            className="text-sm text-gray-900 truncate max-w-[320px] cursor-default block"
                            title={file.file_name}
                          >
                            {file.file_name}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <span className="text-xs text-gray-500 uppercase bg-gray-100 px-2 py-1 rounded">
                            {file.file_type}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500">
                          {formatFileSize(file.file_size)}
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500">
                          {formatTime(file.upload_time)}
                        </td>
                        <td className="px-6 py-3">
                          <button
                            onClick={() => setDetailFile(file)}
                            className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                            title="查看详情"
                          >
                            <Info size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center px-8">
                  {selectedFolder ? (
                    <>
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <FolderOpen size={32} className="text-gray-400" />
                      </div>
                      <p className="text-gray-500 font-medium mb-2">暂无文件</p>
                      <p className="text-gray-400 text-sm">该文件夹内还没有上传任何文件</p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
                        <FolderOpen size={32} className="text-orange-400" />
                      </div>
                      <p className="text-gray-600 font-medium mb-2">选择文件夹查看文件</p>
                      <p className="text-gray-400 text-sm">请在左侧选择一个文件夹，查看其中的知识库文件</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 分页 */}
            {files.length > 0 && pagination.totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  第 {pagination.page} / {pagination.totalPages} 页
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchFiles(selectedFolder.folder_key, pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${pagination.page <= 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => fetchFiles(selectedFolder.folder_key, pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${pagination.page >= pagination.totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 文件详情弹窗 */}
      {detailFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl w-[500px] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-base font-semibold text-gray-900">文件详情</h3>
              <button
                onClick={() => setDetailFile(null)}
                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">文件名</span>
                  <span className="text-sm text-gray-900 break-all">{detailFile.file_name}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">文件类型</span>
                  <span className="text-sm text-gray-900 uppercase">{detailFile.file_type}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">文件大小</span>
                  <span className="text-sm text-gray-900">{detailFile.file_size}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">解析类型</span>
                  <span className="text-sm text-gray-900">{detailFile.parse_type === 1 ? '标准解析' : '深度解析'}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">上传时间</span>
                  <span className="text-sm text-gray-900">{formatTime(detailFile.upload_time)}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">文件ID</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">{detailFile.file_key}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-sm text-gray-500 flex-shrink-0">所属文件夹</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">{detailFile.folder_key}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setDetailFile(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeModal;
